import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { projectMilestones } from '../../milestones';
import type { Milestone } from '../../milestones';
import { withMilestoneForecasts, FORECAST_TRIALS_GLANCE } from '../../milestone-forecast';
import { MilestoneDiamond, MS_TONE, fcTone, planLocalMs, fmtDay } from './milestone';
import { DEFAULT_CALENDAR } from '../../work-calendar';
import {
  packCallouts,
  laneMetrics,
  laneHeight,
  diamondY as diamondYOf,
  chipY,
  overflowY,
  PACK_GEOM,
  type Placement,
} from '../../milestone-pack';
import Icon from '../Icon';
import type { MilestonesLensProps } from './types';

/**
 * THE MILESTONES LENS — the steering timeline (0124). One lane per project, a
 * diamond per milestone across a dated window: "what lands when, across every
 * project," the first question a steering meeting opens with.
 *
 * TWO LABELLED LENSES the viewer switches between (Vinnie, 29 Oct — "keep both as
 * labelled lenses"), because a committed date and a predicted date answer different
 * questions and must never be mistaken for one another:
 *   • COMMITTED PLAN — the dates the team has committed to (the planned day and each
 *     project's phase-target forecast). A slipping/late milestone keeps a hollow ghost
 *     at its planned day, joined by a SOLID coloured segment to its forecast — the
 *     segment length IS the slip between two KNOWN dates.
 *   • PREDICTED (PACE) — each milestone's date as the board's OWN delivery cadence
 *     predicts it: the 85%-confident day (p85) with a faint p50–p95 "likely window"
 *     band behind it (an estimate reads as a RANGE, never a hard point). A drift from
 *     the committed plan shows the plan as a ghost joined by a DOTTED neutral connector
 *     (a predicted drift, not a known one). Too little finished history degrades to an
 *     honest "building — not enough history" marker — never a faked diamond — and a
 *     point with no linked work reads neutral, "from the plan". Collisions are
 *     recomputed on the PREDICTED dates, so a clash can appear or dissolve under pace.
 *
 * A mockup-faithful build of dev-preview/milestones-lenses (approved 29 Oct) over the
 * original dev-preview/portfolio-analytics/01-flagship-timeline. The capacity heat strip
 * and cross-lane dependency arrows are their own increments and are not drawn here yet.
 */

const DAY = 86_400_000;
import { MONTHS } from '../../when';
/** The pinned lane-label column width (matches --ms-label-w) and a per-month floor
 *  so months stay legible; below this the chart scrolls horizontally. */
const LABEL_W = 176;
const MIN_MONTH_W = 96;

type Lens = 'committed' | 'predicted';

/** A plain `YYYY-MM-DD` → UTC ms at midnight, or undefined. */
function dayToMs(day?: string): number | undefined {
  if (!day) return undefined;
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return Date.UTC(y, m - 1, d);
}
/** An instant → "20 Sep", read in LOCAL parts (the basis the pace p85 and a plan day
 *  share — see milestone-forecast `planMs`), so a label never drifts a day from its mark. */
function fmtMs(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
/** The day a milestone sits on the COMMITTED timeline — actual, else forecast, else
 *  plan (ms). */
const committedMs = (m: Milestone): number | undefined =>
  dayToMs(m.actualDay) ?? dayToMs(m.forecastDay) ?? dayToMs(m.plannedDay);

const monthStartMs = (ms: number): number => {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
};
const addMonths = (ms: number, n: number): number => {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1);
};

/** The window presets — a real control (never a decorative dropdown): fit every
 *  milestone, or a fixed horizon from this month. */
type RangeKey = 'fit' | '6m' | '12m';
const RANGE_LABEL: Record<RangeKey, string> = { fit: 'All milestones', '6m': 'Next 6 months', '12m': 'Next 12 months' };

/** One milestone resolved to the chart — its position and how it should draw, under
 *  whichever lens is live. The render path reads only this, so the two lenses share
 *  one drawing and can never drift apart in code. */
interface Node {
  key: string;
  name: string;
  projectName: string;
  golive: boolean;
  posMs: number;
  /** 'diamond' draws the marker; 'pending' the cold-start placeholder; 'untied' the
   *  neutral from-the-plan point. */
  draw: 'diamond' | 'pending' | 'untied';
  /** Diamond colour: the committed state's own tone, the pace tone (fcTone), or
   *  'muted' for a point with no linked work. */
  tone: 'g' | 'a' | 'r' | 'muted';
  /** The committed plan day to show as a ghost when the mark has drifted past it. */
  ghostMs?: number;
  /** How the ghost→mark connector draws: a solid coloured slip (committed, two known
   *  dates) or a dotted neutral drift (predicted). */
  connector?: 'slip' | 'drift';
  /** The p50–p95 likely window behind a ready prediction (predicted lens only). */
  band?: { loMs: number; hiMs: number };
  dateLabel: string;
  /** For an anchored Go-Live (0217): what it rides on, named inline on the chip
   *  ("ends UAT phase" / the milestone name). Absent for a fixed/unanchored go-live. */
  anchorLabel?: string;
}

interface Lane {
  row: MilestonesLensProps['rows'][number];
  nodes: Node[];
}

export default function MilestonesLens({
  rows,
  loading,
  error,
  at,
  phasesByBoard,
  milestonesByBoard,
  issuesByBoard,
  calendar,
  onOpen,
  onRetry,
}: MilestonesLensProps) {
  const now = at ?? Date.now();
  const cal = calendar ?? DEFAULT_CALENDAR;
  // Open on the committed plan — the familiar, safe read (Vinnie, 29 Oct); the viewer
  // switches to Predicted when they want the pace read.
  const [lens, setLens] = useState<Lens>('committed');
  const [range, setRange] = useState<RangeKey>('fit');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close the range menu on a click away or Escape — the app-wide standard.
  useEffect(() => {
    if (!menuOpen) return;
    const away = (e: MouseEvent) => {
      if (!(e.target as Element | null)?.closest?.('.ms-range')) setMenuOpen(false);
    };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', key); };
  }, [menuOpen]);

  // Every project's milestone set — enriched with the cadence forecast only in the
  // Predicted lens, so the Monte-Carlo never runs when the committed plan is showing.
  const allLanes = useMemo<{ row: Lane['row']; milestones: Milestone[] }[]>(
    () =>
      rows
        .map((row) => {
          const base = projectMilestones(row, phasesByBoard.get(row.id) ?? [], milestonesByBoard.get(row.id) ?? [], now, issuesByBoard.get(row.id) ?? []);
          const milestones =
            lens === 'predicted'
              ? withMilestoneForecasts(base, issuesByBoard.get(row.id) ?? [], now, FORECAST_TRIALS_GLANCE, cal)
              : base;
          return { row, milestones };
        })
        .filter((l) => l.milestones.length > 0),
    [rows, phasesByBoard, milestonesByBoard, issuesByBoard, now, lens, cal],
  );

  /** Where a milestone sits under the live lens — the key the window and the nodes agree on. */
  const posOf = (m: Milestone): number | undefined => {
    if (lens === 'committed') return committedMs(m);
    if (m.actualDay) return dayToMs(m.actualDay); // landed — a fact in either lens
    const f = m.forecast;
    if (f && f.ready) return f.p85; // the pace prediction
    // Pending (too little history) and untied (no tie) both read from the plan.
    return m.plannedDay ? planLocalMs(m.plannedDay) : dayToMs(m.forecastDay);
  };

  /** Resolve one milestone to its chart Node under the live lens. */
  const nodeOf = (m: Milestone, posMs: number): Node => {
    const base = { key: m.key, name: m.name, projectName: m.projectName, golive: m.kind === 'golive', posMs };
    if (lens === 'committed') {
      const planMs = dayToMs(m.plannedDay);
      const drifted = (m.state === 'slipping' || m.state === 'late') && planMs != null && planMs < posMs;
      return {
        ...base,
        draw: 'diamond',
        tone: MS_TONE[m.state],
        ghostMs: drifted ? planMs : undefined,
        connector: drifted ? 'slip' : undefined,
        dateLabel: drifted
          ? `${fmtDay(m.plannedDay)} → ${fmtDay(m.forecastDay)}`
          : fmtDay(m.forecastDay ?? m.actualDay ?? m.plannedDay),
      };
    }
    // PREDICTED lens.
    if (m.actualDay) {
      return { ...base, draw: 'diamond', tone: 'g', dateLabel: `${fmtDay(m.actualDay)} · landed` };
    }
    const f = m.forecast;
    if (!f) {
      return { ...base, draw: 'untied', tone: 'muted', dateLabel: `${fmtDay(m.plannedDay)} · no linked work` };
    }
    if (!f.ready) {
      return { ...base, draw: 'pending', tone: 'muted', dateLabel: 'building — not enough history' };
    }
    const planMs = m.plannedDay ? planLocalMs(m.plannedDay) : undefined;
    const drifted = planMs != null && planMs < posMs;
    return {
      ...base,
      draw: 'diamond',
      tone: fcTone(f, m.plannedDay),
      ghostMs: drifted ? planMs : undefined,
      connector: drifted ? 'drift' : undefined,
      band: { loMs: f.p50, hiMs: f.p95 },
      dateLabel: drifted ? `was ${fmtDay(m.plannedDay)} → ${fmtMs(posMs)}` : `${fmtMs(posMs)} · 85%`,
    };
  };

  // The window, and the lanes/nodes that fall inside it.
  const { startMs, endMs, lanes, months, collisions } = useMemo(() => {
    let start: number;
    let end: number;
    if (range === 'fit') {
      const days = allLanes.flatMap((l) => l.milestones.map(posOf)).filter((d): d is number => d != null);
      const lo = Math.min(now, ...(days.length ? days : [now]));
      const hi = Math.max(now, ...(days.length ? days : [now]));
      start = monthStartMs(lo);
      end = addMonths(monthStartMs(hi), 1);
      // A floor of four months, so a project with one near milestone still reads.
      if (end - start < 120 * DAY) end = addMonths(start, 4);
    } else {
      start = monthStartMs(now);
      end = addMonths(start, range === '6m' ? 6 : 12);
    }
    // A gutter on each side so a diamond near the window edge has room for its label.
    const pad = Math.round((end - start) * 0.08);
    start -= pad;
    end += pad;
    const lns: Lane[] = allLanes
      .map((l) => ({
        row: l.row,
        nodes: l.milestones
          .map((m) => {
            const p = posOf(m);
            if (p == null || p < start || p > end) return null;
            const node = nodeOf(m, p);
            // Name the plan anchor inline on the project's own Go-Live chip (0217) — the
            // synthesized header go-live (key 'golive') only. A user's go-live-KIND milestone
            // (key 'ms:…') is a separate checkpoint and is not what the anchor describes.
            return node.key === 'golive' && l.row.goLiveAnchorLabel
              ? { ...node, anchorLabel: l.row.goLiveAnchorLabel }
              : node;
          })
          .filter((n): n is Node => n != null),
      }))
      .filter((l) => l.nodes.length > 0);
    const mos: number[] = [];
    let m0 = monthStartMs(start);
    if (m0 < start) m0 = addMonths(m0, 1);
    for (let m = m0; m < end; m = addMonths(m, 1)) mos.push(m);
    // Same-week go-live collisions, on the LIVE lens's dates — a drawn go-live only
    // (pending/untied points carry no date to clash on). Grouped by the Monday of the
    // week each lands in; a collision is two DIFFERENT projects launching the same week,
    // so each project counts at most once — a board's synthesized header Go-Live and its
    // own go-live-kind milestone are the same launch and must not clash with each other
    // (the header Go-Live wins the slot). Only weeks with more than one project decide.
    const byWeek = new Map<number, Map<string, Node>>();
    for (const l of lns) {
      for (const n of l.nodes) {
        if (!n.golive || n.draw !== 'diamond') continue;
        const dow = (new Date(n.posMs).getUTCDay() + 6) % 7; // Mon = 0
        const monday = n.posMs - dow * DAY;
        let wk = byWeek.get(monday);
        if (!wk) { wk = new Map(); byWeek.set(monday, wk); }
        const existing = wk.get(l.row.id);
        if (!existing || (n.key === 'golive' && existing.key !== 'golive')) wk.set(l.row.id, n);
      }
    }
    const cols = [...byWeek.entries()]
      .map(([monday, wk]) => ({ monday, nodes: [...wk.values()] }))
      .filter((c) => c.nodes.length > 1)
      .sort((a, b) => a.monday - b.monday);
    return { startMs: start, endMs: end, lanes: lns, months: mos, collisions: cols };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLanes, range, now, lens]);

  const pct = (ms: number) => ((ms - startMs) / (endMs - startMs)) * 100;
  const todayIn = now >= startMs && now <= endMs;

  // When the window is far wider than the pane — the Predicted lens can push slipped go-lives
  // months out — land the view on "today" (the action), not on the far-left early history, so
  // the relevant part is on screen without hunting for the scrollbar. Re-runs on lens/range/data.
  const chartRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = chartRef.current;
    if (!el || !todayIn) return;
    const extra = el.scrollWidth - el.clientWidth;
    if (extra <= 8) return; // nothing to scroll
    const canvas = el.firstElementChild as HTMLElement | null;
    const plotW = canvas ? canvas.clientWidth - LABEL_W : el.scrollWidth - LABEL_W;
    const todayX = LABEL_W + (pct(now) / 100) * Math.max(0, plotW);
    el.scrollTo({ left: Math.max(0, Math.min(extra, todayX - el.clientWidth * 0.28)), behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lens, range, startMs, endMs, lanes.length]);

  const predicted = lens === 'predicted';
  const head = (
    <div className="pf-head">
      <div className="pf-head-l">
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--t1)' }}>
          {predicted ? 'What the pace predicts — not a commitment' : 'What the team has committed to'}
        </div>
        <div className="pf-sub">
          {predicted
            ? 'Predicted from delivery pace · one lane per project · each milestone’s 85%-confident day, from the board’s own cadence'
            : 'Committed plan · one lane per project · the planned day and each project’s phase-target forecast'}
        </div>
      </div>
      <div className="pf-head-r">
        <div className="seg" role="group" aria-label="Timeline lens">
          <button type="button" className={`seg-btn${!predicted ? ' active' : ''}`} aria-pressed={!predicted} onClick={() => setLens('committed')}>
            Committed plan
          </button>
          <button type="button" className={`seg-btn${predicted ? ' active' : ''}`} aria-pressed={predicted} onClick={() => setLens('predicted')}>
            Predicted (pace)
          </button>
        </div>
        <div className="ms-range" ref={menuRef}>
          <button type="button" className="btn" aria-haspopup="listbox" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
            {RANGE_LABEL[range]} <Icon name="chevron" size={16} className="ms-caret" />
          </button>
          {menuOpen && (
            <div className="ms-range-menu" role="listbox">
              {(Object.keys(RANGE_LABEL) as RangeKey[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  role="option"
                  aria-selected={range === k}
                  className={`ms-range-opt${range === k ? ' on' : ''}`}
                  onClick={() => { setRange(k); setMenuOpen(false); }}
                >
                  {RANGE_LABEL[k]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (error) {
    return (
      <>
        {head}
        <div className="pf-board" style={{ padding: '16px 14px' }}>
          <div style={{ fontSize: 13, color: 'var(--t2)', marginBottom: 12 }}>The portfolio could not be read. {error}</div>
          <button className="btn" onClick={onRetry}>Try again</button>
        </div>
      </>
    );
  }
  if (loading && rows.length === 0) return <>{head}</>;

  const legend = predicted ? (
    <div className="ms-legend">
      <span className="ms-leg"><MilestoneDiamond state="on-track" /> Predicted day (85% confident)</span>
      <span className="ms-leg"><span className="ms-conf-swatch" /> Likely window (p50–p95)</span>
      <span className="ms-leg"><span className="ms-ghost-swatch" /> Committed plan day</span>
      <span className="ms-leg"><MilestoneDiamond state="on-track" ringed size={16} /> Go-Live (ringed)</span>
      <span className="ms-leg"><span className="ms-pending-swatch" /> Building — not enough history</span>
      <span className="ms-leg"><MilestoneDiamond state="on-track" tone="muted" /> No linked work (from plan)</span>
    </div>
  ) : (
    <div className="ms-legend">
      <span className="ms-leg"><MilestoneDiamond state="on-track" /> On track</span>
      <span className="ms-leg"><MilestoneDiamond state="slipping" /> Slipping</span>
      <span className="ms-leg"><MilestoneDiamond state="late" /> Late</span>
      <span className="ms-leg"><span className="ms-ghost-swatch" /> Planned (drifted from)</span>
      <span className="ms-leg"><MilestoneDiamond state="on-track" ringed size={16} /> Go-Live (ringed)</span>
    </div>
  );

  return (
    <>
      {head}

      {legend}

      {lanes.length === 0 ? (
        <div className="pf-board" style={{ padding: '16px 14px' }}>
          <div style={{ fontSize: 13, color: 'var(--t2)' }}>
            No milestones fall in this window. Set a Go-Live on a project, flag a phase as a milestone, or widen the range.
          </div>
        </div>
      ) : (
        <div className="ms-chart" ref={chartRef}>
         {/* Keyed by lens so a swap fades the canvas in on the shared easing (R32);
             a per-month floor width keeps months legible, scrolling when narrow. */}
         <div key={lens} className="ms-canvas ms-fade" style={{ minWidth: LABEL_W + months.length * MIN_MONTH_W }}>
          {/* The month axis. */}
          <div className="ms-axis">
            {months.map((m) => (
              <span key={m} className="ms-month" style={{ left: `${pct(m)}%` }}>
                {MONTHS[new Date(m).getUTCMonth()]}
                {new Date(m).getUTCMonth() === 0 ? ` ’${String(new Date(m).getUTCFullYear()).slice(2)}` : ''}
              </span>
            ))}
            {todayIn && <span className="ms-today-label" style={{ left: `${pct(now)}%` }}>Today</span>}
          </div>

          {/* The plot: a shared gridline layer behind the lanes, then a lane a row. */}
          <div className="ms-plot">
            <div className="ms-grid" aria-hidden="true">
              {months.map((m) => (
                <span key={m} className="ms-gridline" style={{ left: `${pct(m)}%` }} />
              ))}
              {collisions.map((c) => {
                const monday = c.monday;
                return (
                  <span
                    key={monday}
                    className="ms-collide-band"
                    style={{ left: `${pct(monday)}%`, width: `${pct(monday + 7 * DAY) - pct(monday)}%` }}
                  />
                );
              })}
              {todayIn && <span className="ms-today" style={{ left: `${pct(now)}%` }} />}
            </div>

            {lanes.map((l) => (
              <MilestoneLane key={l.row.id} lane={l} pct={pct} onOpen={onOpen} />
            ))}
          </div>

          {collisions.length > 0 && (
            <div className="ms-collide-note">
              {collisions.map((c) => (
                <span key={c.monday} className="ms-collide-tag">
                  <Icon name="warning" size={13} />
                  {predicted
                    ? `${c.nodes.length} go-lives may collide · same week (predicted) — ${c.nodes.map((n) => n.projectName).join(', ')}`
                    : `${c.nodes.length} Go-Lives · same week — ${c.nodes.map((n) => n.projectName).join(', ')}`}
                </span>
              ))}
            </div>
          )}
         </div>
        </div>
      )}
    </>
  );
}

/**
 * ONE PROJECT'S LANE, laid out with the collision-aware callout packer (0124 readability
 * rework). Diamonds stay pinned on their real dates; each label becomes a compact chip
 * assigned to the nearest collision-free tier above / below the axis and tied to its
 * diamond by a leader; a cluster denser than the tier budget collapses to "+N more".
 *
 * The chip widths are MEASURED from the real DOM (a chip's text width can't be known
 * ahead of render), so the packing is exact. The measure runs in a layout effect — before
 * the browser paints — so a viewer never sees the pre-packed state, and a ResizeObserver
 * re-packs when the chart's width changes (a window resize, the panel reflowing).
 */
function MilestoneLane({
  lane,
  pct,
  onOpen,
}: {
  lane: Lane;
  pct: (ms: number) => number;
  onOpen: (id: string) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const nodes = lane.nodes;
  const [placed, setPlaced] = useState<{
    places: Placement[];
    tiersUp: number;
    tiersDn: number;
    hasOverflow: boolean;
    dY: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const measure = () => {
      const trackW = track.clientWidth;
      const packNodes = nodes.map((n, i) => {
        const el = chipRefs.current[i];
        const w = el ? el.getBoundingClientRect().width : 0;
        return { cx: (pct(n.posMs) / 100) * trackW, w };
      });
      const places = packCallouts(packNodes);
      const m = laneMetrics(places);
      setPlaced({ ...m, places, dY: diamondYOf(m), height: laneHeight(m) });
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(track);
      return () => ro.disconnect();
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, pct]);

  const dY = placed?.dY ?? PACK_GEOM.PADV + PACK_GEOM.AXIS / 2;
  const overflowNodes = placed ? nodes.filter((_, i) => placed.places[i]?.overflow) : [];

  return (
    <div
      className="ms2-lane"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(lane.row.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(lane.row.id); } }}
      title={`Open ${lane.row.name}`}
      style={placed ? { minHeight: placed.height } : undefined}
    >
      <div className="ms2-lane-label">
        <div className="ms2-lane-name">{lane.row.name}</div>
        <div className="ms2-lane-owner">{lane.row.owner}</div>
      </div>
      <div className="ms2-track" ref={trackRef} style={placed ? { height: placed.height } : undefined}>
        {/* Axis furniture — the likely-window band, the plan ghost and its slip/drift
            connector — sits on the diamond band, behind the chips, positioned by date. */}
        {nodes.map((n) => {
          const toneClass = n.tone === 'muted' ? 'muted' : n.tone;
          const bits: JSX.Element[] = [];
          if (n.band) {
            const lo = pct(n.band.loMs);
            const hi = pct(n.band.hiMs);
            bits.push(
              <span
                key={`${n.key}-band`}
                className={`ms-conf-band ${toneClass}`}
                style={{ left: `${lo}%`, width: `${Math.max(0, hi - lo)}%`, top: dY, transform: 'translateY(-50%)' }}
                aria-hidden="true"
              />,
            );
          }
          if (n.ghostMs != null && n.connector) {
            const g = pct(n.ghostMs);
            const p = pct(n.posMs);
            bits.push(
              <span
                key={`${n.key}-seg`}
                className={n.connector === 'slip' ? `ms-slip-seg ${toneClass}` : 'ms-pred-conn'}
                style={{ left: `${g}%`, width: `${Math.max(0, p - g)}%`, top: dY, transform: 'translateY(-50%)' }}
                aria-hidden="true"
              />,
            );
            bits.push(
              <span
                key={`${n.key}-ghost`}
                className="ms-ghost"
                style={{ left: `${g}%`, top: dY, marginTop: -5, transform: 'none' }}
                aria-hidden="true"
              />,
            );
          }
          return bits;
        })}

        {/* The diamonds, pinned on their real dates on the axis band. */}
        {placed && nodes.map((n) => (
          <span key={`${n.key}-dia`} className="ms2-node" style={{ left: `${pct(n.posMs)}%`, top: dY }}>
            {n.draw === 'pending' ? (
              <span className="ms-pending" aria-hidden="true" />
            ) : (
              <MilestoneDiamond state="on-track" tone={n.tone} ringed={n.golive} size={n.golive ? 18 : 13} />
            )}
          </span>
        ))}

        {/* The leaders — a hairline from each placed chip's near edge to its diamond. */}
        {placed && nodes.map((n, i) => {
          const place = placed.places[i];
          if (!place || place.overflow) return null;
          const track = trackRef.current;
          const trackW = track ? track.clientWidth : 0;
          const cx = (pct(n.posMs) / 100) * trackW;
          const cy = chipY(place, dY);
          const chipEdge = place.side === 'up' ? cy + PACK_GEOM.ROW / 2 : cy - PACK_GEOM.ROW / 2;
          const y1 = Math.min(dY, chipEdge) - 1;
          const y2 = Math.max(dY, chipEdge) + 1;
          const el = chipRefs.current[i];
          const half = (el ? el.getBoundingClientRect().width : 0) / 2;
          let chipX = cx;
          if (chipX - half < 2) chipX = half + 2;
          if (trackW && chipX + half > trackW - 2) chipX = trackW - 2 - half;
          const nudged = Math.abs(chipX - cx) > 2;
          return (
            <span key={`${n.key}-lead`} aria-hidden="true">
              <span className="ms2-leader" style={{ left: cx, top: y1, height: Math.max(0, y2 - y1) }} />
              {nudged && (
                <span
                  className="ms2-leader h"
                  style={{ left: Math.min(chipX, cx), top: place.side === 'up' ? dY - 6 : dY + 6, width: Math.abs(chipX - cx) }}
                />
              )}
            </span>
          );
        })}

        {/* The callout chips. All are rendered (so widths can be measured and re-measured);
            once packed, the placed ones are positioned and the overflow ones hidden. */}
        {nodes.map((n, i) => {
          const place = placed?.places[i];
          const toneDate = n.tone === 'a' || n.tone === 'r' ? ` ${n.tone}` : '';
          const mutedCls = n.tone === 'muted' ? ' muted' : '';
          let style: CSSProperties = { visibility: 'hidden', left: 0, top: 0 };
          if (placed && place && !place.overflow) {
            const track = trackRef.current;
            const trackW = track ? track.clientWidth : 0;
            const cx = (pct(n.posMs) / 100) * trackW;
            const el = chipRefs.current[i];
            const half = (el ? el.getBoundingClientRect().width : 0) / 2;
            let chipX = cx;
            if (chipX - half < 2) chipX = half + 2;
            if (trackW && chipX + half > trackW - 2) chipX = trackW - 2 - half;
            style = { left: chipX, top: chipY(place, dY), transform: 'translate(-50%, -50%)', visibility: 'visible' };
          }
          return (
            <div
              key={n.key}
              ref={(el) => { chipRefs.current[i] = el; }}
              className={`ms2-chip${toneDate}${mutedCls}${n.golive ? ' go' : ''}`}
              style={style}
              title={n.name}
            >
              <span className="nm">{n.name}</span> <span className="dt">{n.dateLabel}</span>
              {n.golive && n.anchorLabel && (
                <span className="anchor"><Icon name="link" size={12} className="lnk" />{n.anchorLabel}</span>
              )}
            </div>
          );
        })}

        {/* The overflow count — a reserved row below the last used down tier. Opening the
            project shows every checkpoint; nothing is lost, the diamonds stay on the axis. */}
        {placed && overflowNodes.length > 0 && (() => {
          const track = trackRef.current;
          const trackW = track ? track.clientWidth : 0;
          const meanCx =
            nodes.filter((_, i) => placed.places[i]?.overflow)
              .reduce((s, n) => s + (pct(n.posMs) / 100) * trackW, 0) / overflowNodes.length;
          return (
            <div
              className="ms2-more"
              style={{ left: meanCx, top: overflowY(placed, dY), transform: 'translate(-50%, -50%)' }}
            >
              +{overflowNodes.length} more
            </div>
          );
        })()}
      </div>
    </div>
  );
}
